import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import QuickActionPanel from '../../components/ui/QuickActionPanel';
import LeadDiscoveryPanel from './components/LeadDiscoveryPanel';
import ValidationResults from './components/ValidationResults';
import DataCleansingPanel from './components/DataCleansingPanel';
import BatchProcessingPanel from './components/BatchProcessingPanel';
import QualityMetrics from './components/QualityMetrics';
import FilteringControls from './components/FilteringControls';
import ExportOptions from './components/ExportOptions';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PageLayout from '../../components/ui/PageLayout';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const AILeadFinderAndScrubber = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('discovery');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [validatedLeads, setValidatedLeads] = useState([]);
  const [qualityMetrics, setQualityMetrics] = useState(null);
  const [filterCriteria, setFilterCriteria] = useState({
    validationStatus: 'all',
    dataCompleteness: 'all',
    confidenceLevel: 0
  });
  const [error, setError] = useState(null);
  const [discoveredLeads, setDiscoveredLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      loadLeads();
    } else {
      setLoading(false);
    }
  }, [user?.id]);

  const loadLeads = async () => {
    try {
      setLoading(true);
      const { data, error: leadsError } = await supabase
        ?.from('funnel_leads')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(100);

      if (leadsError) {
        console.warn('Error loading leads:', leadsError?.message);
        setDiscoveredLeads([]);
      } else {
        const transformed = (data || [])?.map(lead => ({
          id: lead?.id,
          name: `${lead?.first_name || ''} ${lead?.last_name || ''}`?.trim() || lead?.email,
          title: lead?.job_title || 'Unknown',
          company: lead?.company || 'Unknown',
          industry: lead?.industry || 'Unknown',
          email: lead?.email || '',
          phone: lead?.phone || '',
          location: lead?.city ? `${lead?.city}, ${lead?.state || ''}` : 'Unknown',
          source: lead?.source || 'Unknown',
          discoveredAt: lead?.created_at,
          validationStatus: lead?.email ? 'validated' : 'needs_review',
          emailVerified: !!lead?.email,
          phoneVerified: !!lead?.phone,
          profileComplete: Math.min(100, [
            lead?.email, lead?.phone, lead?.company, lead?.job_title, lead?.city
          ]?.filter(Boolean)?.length * 20),
          aiConfidence: lead?.lead_score || 50,
          leadScore: lead?.lead_score || 0,
          tags: [],
          duplicateStatus: 'unique',
          dataQuality: (lead?.lead_score || 0) >= 80 ? 'excellent' : (lead?.lead_score || 0) >= 60 ? 'good' : 'needs_review'
        }));
        setDiscoveredLeads(transformed);

        // Calculate quality metrics
        const validated = transformed?.filter(l => l?.validationStatus === 'validated')?.length;
        const total = transformed?.length;
        setQualityMetrics({
          totalLeads: total,
          validatedLeads: validated,
          validationRate: total > 0 ? Math.round((validated / total) * 100) : 0,
          avgScore: total > 0 ? Math.round(transformed?.reduce((s, l) => s + l?.leadScore, 0) / total) : 0,
          duplicates: 0,
          incomplete: transformed?.filter(l => l?.profileComplete < 60)?.length
        });
      }
    } catch (err) {
      console.error('Error loading leads:', err);
      setError('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const handleLeadSelect = (leadId, isSelected) => {
    setSelectedLeads(prev =>
      isSelected ? [...prev, leadId] : prev?.filter(id => id !== leadId)
    );
  };

  const handleSelectAll = (isSelected) => {
    setSelectedLeads(isSelected ? discoveredLeads?.map(l => l?.id) : []);
  };

  const handleFilterChange = (newCriteria) => {
    setFilterCriteria(prev => ({ ...prev, ...newCriteria }));
  };

  const filteredLeads = discoveredLeads?.filter(lead => {
    if (filterCriteria?.validationStatus !== 'all' && lead?.validationStatus !== filterCriteria?.validationStatus) return false;
    if (lead?.aiConfidence < filterCriteria?.confidenceLevel) return false;
    return true;
  });

  const tabs = [
    { id: 'discovery', label: 'Lead Discovery', icon: 'Search' },
    { id: 'validation', label: 'Validation Results', icon: 'CheckCircle' },
    { id: 'cleansing', label: 'Data Cleansing', icon: 'Sparkles' },
    { id: 'batch', label: 'Batch Processing', icon: 'Layers' },
    { id: 'metrics', label: 'Quality Metrics', icon: 'BarChart3' },
    { id: 'export', label: 'Export', icon: 'Download' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'discovery':
        return (
          <LeadDiscoveryPanel
            leads={filteredLeads}
            selectedLeads={selectedLeads}
            onLeadSelect={handleLeadSelect}
            onSelectAll={handleSelectAll}
            isProcessing={isProcessing}
            onRefresh={loadLeads}
            onStartDiscovery={loadLeads}
            onSelectLeads={handleLeadSelect}
          />
        );
      case 'validation':
        return (
          <ValidationResults
            leads={filteredLeads}
            validatedLeads={validatedLeads}
            onValidate={(leads) => setValidatedLeads(leads)}
            onValidateLeads={(leads) => setValidatedLeads(leads)}
            isProcessing={isProcessing}
            onSelectLeads={handleLeadSelect}
          />
        );
      case 'cleansing':
        return (
          <DataCleansingPanel
            leads={filteredLeads}
            onCleansingComplete={loadLeads}
            onCleanseData={loadLeads}
            isProcessing={isProcessing}
          />
        );
      case 'batch':
        return (
          <BatchProcessingPanel
            leads={filteredLeads}
            isProcessing={isProcessing}
            progress={processingProgress}
            onProcess={() => {
              setIsProcessing(true);
              setProcessingProgress(0);
              const interval = setInterval(() => {
                setProcessingProgress(prev => {
                  if (prev >= 100) {
                    clearInterval(interval);
                    setIsProcessing(false);
                    return 100;
                  }
                  return prev + 10;
                });
              }, 500);
            }}
            onStartBatchProcess={() => {
              setIsProcessing(true);
              setProcessingProgress(0);
              const interval = setInterval(() => {
                setProcessingProgress(prev => {
                  if (prev >= 100) {
                    clearInterval(interval);
                    setIsProcessing(false);
                    return 100;
                  }
                  return prev + 10;
                });
              }, 500);
            }}
          />
        );
      case 'metrics':
        return <QualityMetrics metrics={qualityMetrics} leads={filteredLeads} />;
      case 'export':
        return (
          <ExportOptions
            leads={filteredLeads}
            selectedLeads={selectedLeads}
            onExport={() => {}}
            onDeploy={() => {}}
          />
        );
      default:
        return null;
    }
  };

  return (
    <PageLayout>
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">AI Lead Finder & Scrubber</h1>
          <p className="text-muted-foreground">
            Discover, validate, and clean your lead data with AI-powered intelligence
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          {selectedLeads?.length > 0 && (
            <span className="text-sm text-muted-foreground">
              {selectedLeads?.length} selected
            </span>
          )}
          <Button
            variant="outline"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={loadLeads}
          >
            Refresh
          </Button>
          <QuickActionPanel />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6">
        <FilteringControls
          filterCriteria={filterCriteria}
          onFilterChange={handleFilterChange}
          totalLeads={discoveredLeads?.length}
          filteredCount={filteredLeads?.length}
          filters={filterCriteria}
        />
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-border">
          <nav className="-mb-px flex space-x-6 overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading leads...</p>
          </div>
        </div>
      ) : (
        renderTabContent()
      )}
    </PageLayout>
  );
};

export default AILeadFinderAndScrubber;